import React from 'react';
import { trackWidgetEvent } from '../utils/analytics';
import {
  CHAT_WIDGET_AVATAR_STREAM_INITIALIZED,
  CHAT_WIDGET_AVATAR_STREAM_TERMINATED,
  CHAT_WIDGET_AVATAR_SESSION_TIMEDOUT,
  CHAT_WIDGET_AVATAR_CONCURRENCY_CAP_HIT,
  CHAT_WIDGET_AVATAR_STREAM_FAILURE,
} from '@/lib/analytics-events';
import { anamClient, SessionTokenError } from './anamClient';
import { isSpeaking } from './anamEvents';
import {
  AVATAR_IDLE_TIMEOUT_MS,
  classifyAnamTimeout,
} from './avatarDisconnect';
import { createAvatarTabLock } from './avatarTabLock';

export { AVATAR_IDLE_TIMEOUT_MS };

/** Grace after deferred idle while speech ends — allows PTT transcript to arrive. */
export const PENDING_IDLE_STOP_GRACE_MS = 3_000;

export type AvatarSessionState =
  | 'off'
  | 'connecting'
  | 'active'
  | 'textOnly'
  | 'textOnlyLocked'
  | 'reconnecting';

export type AvatarTerminateReason =
  | 'user_toggle'
  | 'max_session'
  | 'idle'
  | 'fallback'
  | 'pagehide';

type AvatarSessionContextValue = {
  state: AvatarSessionState;
  avatarMode: boolean;
  isAvatarStreamActive: boolean;
  showAvatarToggle: boolean;
  enterAvatarMode: () => void;
  exitAvatarMode: (reason?: AvatarTerminateReason) => void;
  connectVideo: (videoElement: HTMLVideoElement) => Promise<void>;
  reattachVideo: (videoElement: HTMLVideoElement) => Promise<void>;
  touchAvatarActivity: () => void;
};

const AvatarSessionContext = React.createContext<AvatarSessionContextValue | null>(null);

type ProviderProps = {
  children: React.ReactNode;
  institutionId: string;
  institutionName?: string | null;
  sessionId: string;
  threadId: string;
  photorealAvatarEnabled: boolean;
  avatarMaxSessionLengthSeconds?: number;
};

export function AvatarSessionProvider({
  children,
  institutionId,
  institutionName,
  sessionId,
  threadId,
  photorealAvatarEnabled,
  avatarMaxSessionLengthSeconds = 600,
}: ProviderProps) {
  const [state, setState] = React.useState<AvatarSessionState>('off');
  const stateRef = React.useRef(state);
  const tabLock = React.useMemo(() => createAvatarTabLock(institutionId), [institutionId]);

  const setSessionState = React.useCallback((next: AvatarSessionState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const sessionStartedAtRef = React.useRef<number | null>(null);
  const lastActivityAtRef = React.useRef<number | null>(null);
  const idleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxSessionTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMaxSessionStopRef = React.useRef(false);
  const pendingIdleStopRef = React.useRef(false);
  const pendingIdleGraceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectGenerationRef = React.useRef(0);
  const videoElementRef = React.useRef<HTMLVideoElement | null>(null);

  const apiEndpoint = import.meta.env.VITE_CHAT_API_ENDPOINT ?? '';

  const baseEventProps = React.useCallback(
    () => ({
      institutionId,
      ...(institutionName ? { institutionName } : {}),
      sessionId,
      threadId,
    }),
    [institutionId, institutionName, sessionId, threadId],
  );

  const clearPendingIdleGrace = React.useCallback(() => {
    if (pendingIdleGraceTimerRef.current) {
      clearTimeout(pendingIdleGraceTimerRef.current);
      pendingIdleGraceTimerRef.current = null;
    }
  }, []);

  const clearTimers = React.useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (maxSessionTimerRef.current) {
      clearTimeout(maxSessionTimerRef.current);
      maxSessionTimerRef.current = null;
    }
    clearPendingIdleGrace();
    pendingMaxSessionStopRef.current = false;
    pendingIdleStopRef.current = false;
  }, [clearPendingIdleGrace]);

  const disposeStream = React.useCallback(
    async (opts?: { keepTimers?: boolean }) => {
      if (!opts?.keepTimers) {
        clearTimers();
      }
      tabLock.release();
      await anamClient.dispose();
    },
    [clearTimers, tabLock],
  );

  const fallbackToTextOnly = React.useCallback(
    (nextState: 'textOnly' | 'textOnlyLocked' = 'textOnly') => {
      pendingMaxSessionStopRef.current = false;
      pendingIdleStopRef.current = false;
      connectGenerationRef.current += 1;
      setSessionState(nextState);
      void disposeStream();
    },
    [disposeStream, setSessionState],
  );

  const trackTerminated = React.useCallback(
    (reason: AvatarTerminateReason) => {
      trackWidgetEvent(CHAT_WIDGET_AVATAR_STREAM_TERMINATED, {
        ...baseEventProps(),
        reason,
      });
    },
    [baseEventProps],
  );

  const finishMaxSessionStop = React.useCallback(() => {
    const startedAt = sessionStartedAtRef.current ?? Date.now();
    trackWidgetEvent(CHAT_WIDGET_AVATAR_SESSION_TIMEDOUT, {
      ...baseEventProps(),
      timeoutType: 'max_session',
      durationMs: Date.now() - startedAt,
    });
    trackTerminated('max_session');
    sessionStartedAtRef.current = null;
    lastActivityAtRef.current = null;
    fallbackToTextOnly('textOnly');
  }, [baseEventProps, fallbackToTextOnly, trackTerminated]);

  const finishIdleStop = React.useCallback(() => {
    const startedAt = sessionStartedAtRef.current ?? Date.now();
    trackWidgetEvent(CHAT_WIDGET_AVATAR_SESSION_TIMEDOUT, {
      ...baseEventProps(),
      timeoutType: 'idle',
      durationMs: Date.now() - startedAt,
    });
    trackTerminated('idle');
    sessionStartedAtRef.current = null;
    lastActivityAtRef.current = null;
    fallbackToTextOnly('textOnly');
  }, [baseEventProps, fallbackToTextOnly, trackTerminated]);

  const scheduleMaxSessionStop = React.useCallback(() => {
    if (maxSessionTimerRef.current) clearTimeout(maxSessionTimerRef.current);
    maxSessionTimerRef.current = setTimeout(() => {
      pendingMaxSessionStopRef.current = true;
      if (!isSpeaking()) {
        finishMaxSessionStop();
      }
    }, avatarMaxSessionLengthSeconds * 1000);
  }, [avatarMaxSessionLengthSeconds, finishMaxSessionStop]);

  const scheduleIdleStop = React.useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (stateRef.current !== 'active') return;
    clearPendingIdleGrace();
    pendingIdleStopRef.current = false;
    idleTimerRef.current = setTimeout(() => {
      if (isSpeaking()) {
        pendingIdleStopRef.current = true;
        return;
      }
      finishIdleStop();
    }, AVATAR_IDLE_TIMEOUT_MS);
  }, [clearPendingIdleGrace, finishIdleStop]);

  const schedulePendingIdleStopGrace = React.useCallback(() => {
    clearPendingIdleGrace();
    pendingIdleGraceTimerRef.current = setTimeout(() => {
      pendingIdleGraceTimerRef.current = null;
      if (pendingIdleStopRef.current && stateRef.current === 'active') {
        finishIdleStop();
      }
    }, PENDING_IDLE_STOP_GRACE_MS);
  }, [clearPendingIdleGrace, finishIdleStop]);

  const touchAvatarActivity = React.useCallback(() => {
    if (stateRef.current !== 'active') return;
    lastActivityAtRef.current = Date.now();
    scheduleIdleStop();
  }, [scheduleIdleStop]);

  const handleConcurrencyCap = React.useCallback(() => {
    trackWidgetEvent(CHAT_WIDGET_AVATAR_CONCURRENCY_CAP_HIT, baseEventProps());
    trackTerminated('fallback');
    sessionStartedAtRef.current = null;
    lastActivityAtRef.current = null;
    fallbackToTextOnly('textOnlyLocked');
  }, [baseEventProps, fallbackToTextOnly, trackTerminated]);

  const handleConnectFailure = React.useCallback(
    (err: unknown, phase: 'connect' | 'mid_session', attemptedReconnect: boolean) => {
      const isTokenError = err instanceof SessionTokenError;
      const lockSession =
        isTokenError &&
        (err.code === 'CONCURRENCY_CAP' ||
          err.code === 'INVALID_CREDENTIALS' ||
          err.code === 'DISABLED');

      if (isTokenError && err.code === 'CONCURRENCY_CAP') {
        trackWidgetEvent(CHAT_WIDGET_AVATAR_CONCURRENCY_CAP_HIT, baseEventProps());
      } else {
        trackWidgetEvent(CHAT_WIDGET_AVATAR_STREAM_FAILURE, {
          ...baseEventProps(),
          phase,
          attemptedReconnect,
        });
      }

      trackTerminated('fallback');
      sessionStartedAtRef.current = null;
      lastActivityAtRef.current = null;
      fallbackToTextOnly(lockSession ? 'textOnlyLocked' : 'textOnly');
    },
    [baseEventProps, fallbackToTextOnly, trackTerminated],
  );

  const onStreamConnected = React.useCallback(() => {
    const now = Date.now();
    sessionStartedAtRef.current = now;
    lastActivityAtRef.current = now;
    tabLock.acquire();
    setSessionState('active');
    trackWidgetEvent(CHAT_WIDGET_AVATAR_STREAM_INITIALIZED, baseEventProps());
    scheduleMaxSessionStop();
    scheduleIdleStop();
  }, [baseEventProps, scheduleIdleStop, scheduleMaxSessionStop, setSessionState, tabLock]);

  const connectVideoInternal = React.useCallback(
    async (videoElement: HTMLVideoElement, isReconnect: boolean) => {
      if (tabLock.isHeldByOtherTab()) {
        handleConcurrencyCap();
        return;
      }
      const generation = ++connectGenerationRef.current;
      try {
        await anamClient.init({ apiEndpoint, institutionId });
        if (generation !== connectGenerationRef.current) return;
        await anamClient.attach(videoElement);
        if (generation !== connectGenerationRef.current) return;
        onStreamConnected();
      } catch (err) {
        if (generation !== connectGenerationRef.current) return;
        await handleConnectFailure(err, isReconnect ? 'mid_session' : 'connect', isReconnect);
      }
    },
    [
      apiEndpoint,
      handleConcurrencyCap,
      handleConnectFailure,
      institutionId,
      onStreamConnected,
      tabLock,
    ],
  );

  /** Silent text-only fallback for unexpected mid-session disconnect (no reconnect UI). */
  const handleUnexpectedDisconnect = React.useCallback(() => {
    handleConnectFailure(new Error('Connection closed'), 'mid_session', false);
  }, [handleConnectFailure]);

  const handleConnectionClosed = React.useCallback(() => {
    if (stateRef.current !== 'active') return;

    const classifierInput = {
      sessionStartedAt: sessionStartedAtRef.current,
      lastActivityAt: lastActivityAtRef.current,
      pendingMaxSessionStop: pendingMaxSessionStopRef.current,
      avatarMaxSessionLengthSeconds,
      idleTimerPending: idleTimerRef.current != null,
      maxSessionTimerPending: maxSessionTimerRef.current != null,
    };

    const timeoutKind = classifyAnamTimeout(classifierInput);
    if (timeoutKind === 'max_session') {
      finishMaxSessionStop();
      return;
    }
    if (timeoutKind === 'idle') {
      finishIdleStop();
      return;
    }
    if (tabLock.isHeldByOtherTab()) {
      handleConcurrencyCap();
      return;
    }
    handleUnexpectedDisconnect();
  }, [
    avatarMaxSessionLengthSeconds,
    finishIdleStop,
    finishMaxSessionStop,
    handleConcurrencyCap,
    handleUnexpectedDisconnect,
    tabLock,
  ]);

  React.useEffect(() => {
    const unsubSpeaking = anamClient.onSpeakingChange((speaking) => {
      if (speaking) {
        touchAvatarActivity();
        return;
      }
      if (pendingMaxSessionStopRef.current) {
        finishMaxSessionStop();
      }
      if (pendingIdleStopRef.current) {
        schedulePendingIdleStopGrace();
      }
    });
    return unsubSpeaking;
  }, [finishMaxSessionStop, schedulePendingIdleStopGrace, touchAvatarActivity]);

  React.useEffect(() => {
    const unsubTranscript = anamClient.onUserTranscript((text) => {
      if (!text.trim()) return;
      touchAvatarActivity();
    });
    return unsubTranscript;
  }, [touchAvatarActivity]);

  React.useEffect(() => {
    const unsubClosed = anamClient.onConnectionClosed(() => {
      handleConnectionClosed();
    });
    return unsubClosed;
  }, [handleConnectionClosed]);

  React.useEffect(() => {
    const handlePageHide = () => {
      if (stateRef.current === 'off' || stateRef.current === 'textOnly' || stateRef.current === 'textOnlyLocked') {
        return;
      }
      trackTerminated('pagehide');
      sessionStartedAtRef.current = null;
      lastActivityAtRef.current = null;
      fallbackToTextOnly('textOnly');
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      void disposeStream();
    };
  }, [disposeStream, fallbackToTextOnly, trackTerminated]);

  const connectVideo = React.useCallback(
    async (videoElement: HTMLVideoElement) => {
      videoElementRef.current = videoElement;
      if (stateRef.current === 'connecting') {
        await connectVideoInternal(videoElement, false);
      }
    },
    [connectVideoInternal],
  );

  const reattachVideo = React.useCallback(
    async (videoElement: HTMLVideoElement) => {
      if (stateRef.current !== 'active') return;
      videoElementRef.current = videoElement;
      try {
        await anamClient.attach(videoElement);
      } catch {
        handleConnectionClosed();
      }
    },
    [handleConnectionClosed],
  );

  const enterAvatarMode = React.useCallback(() => {
    if (!photorealAvatarEnabled) return;
    if (stateRef.current === 'textOnlyLocked') return;
    if (stateRef.current === 'active' || stateRef.current === 'connecting') {
      return;
    }
    if (tabLock.isHeldByOtherTab()) {
      handleConcurrencyCap();
      return;
    }
    setSessionState('connecting');
  }, [handleConcurrencyCap, photorealAvatarEnabled, setSessionState, tabLock]);

  const exitAvatarMode = React.useCallback(
    (reason: AvatarTerminateReason = 'user_toggle') => {
      if (
        stateRef.current === 'off' ||
        stateRef.current === 'textOnly' ||
        stateRef.current === 'textOnlyLocked'
      ) {
        return;
      }
      trackTerminated(reason);
      sessionStartedAtRef.current = null;
      lastActivityAtRef.current = null;
      fallbackToTextOnly('textOnly');
    },
    [fallbackToTextOnly, trackTerminated],
  );

  const avatarMode = state === 'connecting' || state === 'active';
  const isAvatarStreamActive = state === 'active';
  const showAvatarToggle = photorealAvatarEnabled && state !== 'textOnlyLocked';

  const value = React.useMemo(
    () => ({
      state,
      avatarMode,
      isAvatarStreamActive,
      showAvatarToggle,
      enterAvatarMode,
      exitAvatarMode,
      connectVideo,
      reattachVideo,
      touchAvatarActivity,
    }),
    [
      state,
      avatarMode,
      isAvatarStreamActive,
      showAvatarToggle,
      enterAvatarMode,
      exitAvatarMode,
      connectVideo,
      reattachVideo,
      touchAvatarActivity,
    ],
  );

  return (
    <AvatarSessionContext.Provider value={value}>{children}</AvatarSessionContext.Provider>
  );
}

export function useAvatarSession(): AvatarSessionContextValue {
  const ctx = React.useContext(AvatarSessionContext);
  if (!ctx) {
    throw new Error('useAvatarSession must be used within AvatarSessionProvider');
  }
  return ctx;
}
